import React, { memo } from 'react';
import { CTAButton } from './CTAButton';
import { TestimonialCard } from './TestimonialCard';
export const AdvertorialPage = () => {
  return <div className="max-w-4xl mx-auto px-4 py-6 text-gray-800" data-id="element-3">
      {/* Headline */}
      <h1 className="text-3xl md:text-4xl font-bold text-red-700 mb-4 leading-tight" data-id="element-4">
        How One Wife's Late-Night Research Saved Her Husband From Knee Surgery
      </h1>
      <p className="text-xl md:text-2xl mb-8 font-medium text-gray-700" data-id="element-5">
        Doctors gave him an ultimatum: "surgery or a wheelchair." She refused to
        accept either. What she found next left his surgeon impressed—and got
        them their life back.
      </p>
      {/* Hero Image */}
      <div className="mb-8 rounded-lg overflow-hidden mx-auto w-1/2" data-id="element-6">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rKL1qTWh96J8vNFvEsF29H/photo_1.png" alt="Sarah and Bob sitting together on their porch, both smiling, Bob standing without assistance" className="w-full h-auto" data-id="element-7" />
      </div>
      {/* Story Section 1 */}
      <div className="text-lg md:text-xl space-y-6 mb-10" data-id="element-8">
        <p data-id="element-9">Sarah Martinez thought she was losing her husband.</p>
        <p data-id="element-10">
          Not to another woman or a midlife crisis. But to the chronic knee pain
          that was stealing the man she married, one agonizing day at a time.
        </p>
        <p data-id="element-11">
          "I watched the person I'd loved for 34 years disappear before my
          eyes," Sarah recalls, her voice still heavy with the memory. "The man
          who built our deck with his own hands couldn't even walk to the
          mailbox without wincing in pain."
        </p>
        <p data-id="element-12">
          Bob, now 67, had been battling knee arthritis for three years. What
          started as occasional stiffness had progressed into debilitating pain
          that controlled their entire lives. "I felt helpless," Sarah says.
          "He'd try to hide how much it hurt, but I could see it in his eyes
          every single day."
        </p>
        <p data-id="element-13">
          By spring of last year, Bob's condition had deteriorated so
          dramatically that his orthopedic surgeon delivered the ultimatum:
        </p>
        <p className="font-bold italic text-center py-4" data-id="element-14">
          "Frankly, Mr. Martinez, we're at a crossroads. It's either knee
          replacement surgery or, eventually, a wheelchair."
        </p>
        <p data-id="element-15">The words chilled Sarah to the bone.</p>
        <p data-id="element-16">
          "I wasn't ready to become a full-time caregiver," she admits. "That
          sounds selfish, but we worked our whole lives for this time. We had
          plans to travel, to play with our grandkids. A major surgery with a
          4-6 month recovery and no guarantee of success... it felt like a
          prison sentence for both of us."
        </p>
        <p data-id="element-17">
          Her fears were valid. Studies show nearly 1 in 3 knee replacement
          patients still experience chronic pain years later.
        </p>
        <p data-id="element-18">
          "I just kept thinking," Sarah says, "'There has to be another way.'"
        </p>
      </div>
      {/* Image Section */}
      <div className="mb-8 rounded-lg overflow-hidden mx-auto w-1/2" data-id="element-19">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/aQozZmSURQnMtMdMw7tVT9/photo_2.png" alt="Senior woman researching health solutions on tablet at night" className="w-full h-auto" data-id="element-20" />
      </div>
      {/* Section 2 */}
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-800" data-id="element-21">
        The 2 AM Research That Changed Everything
      </h2>
      <div className="text-lg md:text-xl space-y-6 mb-10" data-id="element-22">
        <p data-id="element-23">
          One night, unable to sleep, Sarah found herself at her computer,
          digging through medical journals and arthritis forums. She'd already
          spent a fortune on the standard recommendations: prescription NSAIDs
          that upset Bob's stomach, three rounds of cortisone shots at $800 a
          pop, and physical therapy that was often too painful for him to
          complete.
        </p>
        <p data-id="element-24">
          "I started seeing a pattern," Sarah recalls. "I realized we were only
          fighting symptoms, not the root problems."
        </p>
        <p data-id="element-25">
          Her research pointed to a powerful conclusion: chronic knee pain
          wasn't one problem, but three.
        </p>
        <div className="bg-yellow-50 p-5 rounded-lg my-6" data-id="element-26">
          <p className="font-bold mb-2" data-id="element-27">Poor Circulation:</p>
          <p data-id="element-28">
            Stiff, arthritic joints don't get enough blood flow, starving the
            cartilage of the nutrients it needs to heal.
          </p>
          <p className="font-bold mb-2 mt-4" data-id="element-29">Chronic Inflammation:</p>
          <p data-id="element-30">
            The body's own immune response creates constant, low-grade
            inflammation, causing pain and further tissue damage.
          </p>
          <p className="font-bold mb-2 mt-4" data-id="element-31">Muscle Tension & Scar Tissue:</p>
          <p data-id="element-32">
            The muscles around the knee lock up to protect the joint, creating
            stiffness and reducing the range of motion.
          </p>
        </div>
        <p data-id="element-33">
          "No wonder nothing worked!" Sarah says. "The pain pills just masked
          the problem. The shots only helped with inflammation for a few weeks.
          The PT was too painful because of the tension. We were fighting a
          three-front war with a one-front solution."
        </p>
        <p data-id="element-34">
          That's when she found it. A device called ThermaLure that didn't just
          promise to do one thing—it was engineered to tackle all three root
          causes at once using a combination of proven therapies.
        </p>
        <ul className="list-disc pl-6 space-y-2" data-id="element-35">
          <li data-id="element-36">
            <span className="font-bold" data-id="element-37">Therapeutic Heat</span> to boost
            circulation and soothe the joint.
          </li>
          <li data-id="element-38">
            <span className="font-bold" data-id="element-39">Targeted Vibration Massage</span> to
            release muscle tension and break up scar tissue.
          </li>
          <li data-id="element-40">
            <span className="font-bold" data-id="element-41">
              Clinical-Strength Red Light Therapy
            </span>{' '}
            to reduce inflammation at the cellular level.
          </li>
        </ul>
        <p data-id="element-42">
          "I was skeptical," Sarah admits. "But the science just made sense. It
          wasn't magic; it was a comprehensive approach. For the first time, I
          felt a glimmer of real hope."
        </p>
      </div>
      {/* Image Section */}
      <div className="mb-2 rounded-lg overflow-hidden mx-auto w-1/2" data-id="element-43">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2q4QXfzHNXG4UDXP2WmG86/photo_3.png" alt="Senior man in hospital bed with knee bandage" className="w-full h-auto" data-id="element-44" />
      </div>
      <p className="text-center italic text-gray-600 mb-8" data-id="element-45">
        Bob's $800 cortisone injections quickly added up.
      </p>
      {/* Section 3 */}
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-800" data-id="element-46">
        The Transformation That Amazed Them Both
      </h2>
      <div className="text-lg md:text-xl space-y-6 mb-10" data-id="element-47">
        <p data-id="element-48">
          Sarah ordered ThermaLure on a Tuesday. It arrived Thursday. Bob
          reluctantly agreed to try it "just to make her happy." What happened
          next amazed them both.
        </p>
        <p data-id="element-49">
          <span className="font-bold" data-id="element-50">Day 1:</span> "The first time Bob strapped
          it on, he said it felt like 'a warm hug for my knee.' The heat was
          deeper and more soothing than any heating pad. After 35 minutes, he
          stood up without his usual grimace."
        </p>
        <p data-id="element-51">
          <span className="font-bold" data-id="element-52">Week 1:</span> "Bob was using it twice a
          day. I noticed he wasn't reaching for the ibuprofen bottle every few
          hours. When I asked him, he said, 'I think this thing might actually
          be helping.'"
        </p>
        <p data-id="element-53">
          <span className="font-bold" data-id="element-54">Week 2:</span> "Bob walked to the end of
          our street and back—something he hadn't done in over a year. When he
          came through the door, he was smiling. Actually smiling. I started
          crying right there."
        </p>
        <p data-id="element-55">
          <span className="font-bold" data-id="element-56">Month 2:</span> "We went back for Bob's
          pre-op appointment. We told the surgeon we wanted to cancel. He was
          skeptical, of course. But then he examined Bob. He tested his flexion
          and extension, watched him walk down the hall, and checked the
          swelling. He looked at Bob's chart, then back at Bob. He shook his
          head and said, 'Well, I don't know what you're doing, Mr. Martinez,
          but it's clearly working. Keep it up.'"
        </p>
        <p data-id="element-57">
          Today, nearly a year later, Bob is off pain medication completely.
          Last month, they took their first vacation in three years—a walking
          tour of Ireland.
        </p>
      </div>
      {/* Image Section */}
      <div className="mb-8 rounded-lg overflow-hidden mx-auto w-1/2" data-id="element-58">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/webbieeR9bCXZiNkT2Djgg/photo_4.png" alt="Senior couple smiling in front of a castle in Ireland" className="w-full h-auto" data-id="element-59" />
      </div>
      {/* Section 4 */}
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-800" data-id="element-60">
        The Smart Solution for Knee Pain
      </h2>
      <div className="text-lg md:text-xl space-y-6 mb-10" data-id="element-61">
        <p data-id="element-62">
          ThermaLure works because it provides three therapies in one
          easy-to-use device, addressing the root causes of knee pain
          simultaneously.
        </p>
        <div className="overflow-x-auto mb-6" data-id="element-63">
          <table className="w-full border-collapse" data-id="element-64">
            <thead data-id="element-65">
              <tr className="bg-blue-100" data-id="element-66">
                <th className="border border-gray-300 px-4 py-2 text-left" data-id="element-67">
                  Problem
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left" data-id="element-68">
                  ThermaLure Solution
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left" data-id="element-69">
                  Benefit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-70">
              <tr data-id="element-71">
                <td className="border border-gray-300 px-4 py-2" data-id="element-72">
                  Poor Circulation
                </td>
                <td className="border border-gray-300 px-4 py-2" data-id="element-73">
                  Therapeutic Heat
                </td>
                <td className="border border-gray-300 px-4 py-2" data-id="element-74">
                  Increases blood flow to nourish cartilage.
                </td>
              </tr>
              <tr className="bg-gray-50" data-id="element-75">
                <td className="border border-gray-300 px-4 py-2" data-id="element-76">
                  Muscle Tension
                </td>
                <td className="border border-gray-300 px-4 py-2" data-id="element-77">
                  Penetrating Vibration
                </td>
                <td className="border border-gray-300 px-4 py-2" data-id="element-78">
                  Releases stiff muscles & improves flexibility.
                </td>
              </tr>
              <tr data-id="element-79">
                <td className="border border-gray-300 px-4 py-2" data-id="element-80">
                  Inflammation
                </td>
                <td className="border border-gray-300 px-4 py-2" data-id="element-81">
                  Red Light Therapy
                </td>
                <td className="border border-gray-300 px-4 py-2" data-id="element-82">
                  Reduces pain & swelling at a cellular level.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p data-id="element-83">
          By addressing all three issues at once, in the comfort of your own
          home, you give your body the tools it needs to truly start feeling
          better.
        </p>
      </div>
      {/* Testimonials */}
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-800" data-id="element-84">
        Real People, Real Results
      </h2>
      <div className="mb-10" data-id="element-85">
        <TestimonialCard quote="My husband was so stubborn about his knee pain. After seeing how much ThermaLure helped our neighbor, I ordered one without telling him. Three weeks later, he was back to doing yard work and thanked me for 'forcing' him to try it. Best $200 I ever spent on our marriage." author="Margaret T." data-id="element-86" />
        {/* Image Section */}
        <div className="mb-8 rounded-lg overflow-hidden mx-auto w-1/2" data-id="element-87">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/wkxxMMjC5dHcJBUzkcj5xp/photo_5.png" alt="Senior man gardening with a rake and gardening gloves" className="w-full h-auto" data-id="element-88" />
        </div>
        <TestimonialCard quote="I was terrified my husband would become completely dependent on me after knee surgery. ThermaLure helped him avoid it altogether. He's back to being my partner, not my patient." author="Linda S." data-id="element-89" />
        <TestimonialCard quote="My wife researched this for months. I thought she was wasting our money, but I was wrong. This device gave me back my independence and gave my wife back her husband. We're both grateful." author="James R." data-id="element-90" />
      </div>
      {/* Is it right for you */}
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-800" data-id="element-91">
        Is ThermaLure Right for Your Loved One?
      </h2>
      <div className="text-lg md:text-xl space-y-6 mb-10" data-id="element-92">
        <p data-id="element-93">
          If you're thinking about someone you love who's struggling, here are
          the signs ThermaLure could be the solution you've been looking for:
        </p>
        <ul className="list-disc pl-6 space-y-3" data-id="element-94">
          <li data-id="element-95">
            Daily activities like using stairs or getting up from chairs are
            difficult.
          </li>
          <li data-id="element-96">Sleep is disrupted by constant knee pain.</li>
          <li data-id="element-97">
            They've tried multiple treatments (meds, shots, therapy) with
            limited success.
          </li>
          <li data-id="element-98">
            They're avoiding activities they used to enjoy, like gardening or
            playing with grandkids.
          </li>
          <li data-id="element-99">
            Surgery has been mentioned, but they are hesitant about the risks
            and recovery.
          </li>
          <li data-id="element-100">They're 45 or older with arthritis or "wear and tear" pain.</li>
        </ul>
      </div>
      {/* FAQ */}
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-800" data-id="element-101">
        Answering Your Questions
      </h2>
      <div className="mb-10" data-id="element-102">
        <div className="bg-blue-50 p-5 rounded-lg mb-6" data-id="element-103">
          <p className="font-bold mb-2" data-id="element-104">
            "I've tried everything—this won't work either."
          </p>
          <p className="text-lg" data-id="element-105">
            This is exactly what Bob said. The difference is that ThermaLure
            combines three proven therapies that work synergistically. You
            haven't tried this specific combination before.
          </p>
        </div>
        <div className="bg-blue-50 p-5 rounded-lg mb-6" data-id="element-106">
          <p className="font-bold mb-2" data-id="element-107">"Is it safe for severe arthritis?"</p>
          <p className="text-lg" data-id="element-108">
            ThermaLure is designed for arthritis sufferers. The heat, vibration,
            and light are non-invasive therapies used safely in medical
            settings. Many users with bone-on-bone arthritis find significant
            relief.
          </p>
        </div>
        <div className="bg-blue-50 p-5 rounded-lg mb-6" data-id="element-109">
          <p className="font-bold mb-2" data-id="element-110">
            "We can't afford another expensive gadget."
          </p>
          <p className="text-lg" data-id="element-111">
            Consider the ongoing cost of medications, cortisone shots ($800+
            each), and potential surgery ($30,000+). ThermaLure costs less than
            two cortisone shots for lasting, at-home relief.
          </p>
        </div>
        <div className="bg-blue-50 p-5 rounded-lg mb-6" data-id="element-112">
          <p className="font-bold mb-2" data-id="element-113">"Is it easy to use?"</p>
          <p className="text-lg" data-id="element-114">
            It was designed with seniors in mind. One-button operation and clear
            instructions. If they can use a TV remote, they can use ThermaLure.
          </p>
        </div>
      </div>
      {/* Final CTA */}
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-800" data-id="element-115">
        Don't Let Another Day Pass
      </h2>
      <div className="text-lg md:text-xl space-y-6 mb-10" data-id="element-116">
        <p data-id="element-117">
          "I almost lost my husband to knee pain," Sarah reflects. "Not just
          physically, but emotionally. He was becoming frustrated, dependent,
          and depressed. ThermaLure didn't just help his knees; it gave me back
          the man I married."
        </p>
        <p data-id="element-118">Your moment of decision is now.</p>
        <p data-id="element-119">
          You can close this page and accept that knee pain is an inevitable
          part of life. You can continue watching your loved one struggle,
          dreading the day surgery becomes the only option.
        </p>
        <p data-id="element-120">
          Or you can take the same courageous step Sarah took and give them the
          gift of relief.
        </p>
        <p data-id="element-121">
          You're not just buying a device. You're investing in independence, in
          shared activities, in a future with less pain. You are taking back
          control. With a 90-day, risk-free money-back guarantee, you have
          nothing to lose except their pain.
        </p>
      </div>
      <div className="bg-red-100 p-6 rounded-lg mb-10" data-id="element-122">
        <h2 className="text-2xl md:text-3xl font-bold mb-4 text-center text-red-800" data-id="element-123">
          Give Your Loved One the Gift of Pain-Free Living
        </h2>
        <CTAButton text="Order ThermaLure Now" className="mb-6" data-id="element-124" />
        <div className="text-lg space-y-3" data-id="element-125">
          <p className="font-bold" data-id="element-126">What you get:</p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-127">
            <li data-id="element-128">ThermaLure 3-in-1 Knee Therapy System</li>
            <li data-id="element-129">Rechargeable Battery with 4+ Hour Life</li>
            <li data-id="element-130">Adjustable Comfort Straps (Fits All Sizes)</li>
            <li data-id="element-131">Complete Instruction Guide</li>
            <li data-id="element-132">
              30-Day "Better Than The Last Treatment" Money-Back Guarantee
            </li>
            <li data-id="element-133">Free Shipping Anywhere in the US</li>
          </ul>
          <p className="mt-6 italic" data-id="element-134">
            Remember: You're not just buying a device—you're investing in your
            loved one's comfort, independence, and quality of life.
          </p>
          <p className="font-bold mt-6" data-id="element-135">
            Don't wait. Every day you hesitate is another day of unnecessary
            suffering. Order ThermaLure now and start your own transformation
            story.
          </p>
        </div>
        <CTAButton text="Get ThermaLure Now" className="mt-6" data-id="element-136" />
      </div>
      {/* Footer */}
      <div className="text-center text-gray-500 text-sm mt-10 border-t pt-6" data-id="element-137">
        <p data-id="element-138">
          Questions? Contact our customer care team{' '}
          <a href="#" className="text-blue-600 hover:underline" data-id="element-139">
            HERE
          </a>
        </p>
        <p className="mt-6 text-xs" data-id="element-140">
          Medical Disclaimer: ThermaLure is designed for general wellness and
          pain relief. Results may vary. This device is not intended to
          diagnose, treat, cure, or prevent any disease. Consult your healthcare
          provider before starting any new pain management regimen.
        </p>
      </div>
    </div>;
};